import { Duration, Instant } from "@js-joda/core";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ButtonGroup from "../../components/ButtonGroup";
import Page from "../../components/Page";
import PageHeader from "../../components/PageHeader";
import { usePatientPopulationAtom } from "../../data/store";
import DiabetesPopulationList from "./DiabetesPopulationList";
import HypertensionPopulationList from "./HypertensionPopulationList";

const PatientListPage = () => {
  const [selectedPopulation, setSelectedPopulation] = usePatientPopulationAtom();
  const queryClient = useQueryClient();

  return (
    <Page>
      <div className="pb-8">
        <div className="mb-4 flex items-center gap-4">
          <PageHeader
            title="Patient overview"
            className="ms-[-2px] inline-block overflow-visible text-nowrap"
          />
          <ButtonGroup
            aria-label="Patient population"
            values={[
              { value: "diabetes" as const, title: "Diabetes" },
              { value: "hypertension" as const, title: "Hypertension" },
            ]}
            value={selectedPopulation}
            onChange={setSelectedPopulation}
          />
          <LastUpdatedWidget queryClient={queryClient} queryKey={["patients"]} />
        </div>
        {selectedPopulation === "diabetes" && <DiabetesPopulationList />}
        {selectedPopulation === "hypertension" && <HypertensionPopulationList />}
      </div>
    </Page>
  );
};

type LastUpdatedWidgetProps = {
  queryClient: QueryClient;
  queryKey: string[];
};

const LastUpdatedWidget = ({ queryClient, queryKey }: LastUpdatedWidgetProps) => {
  const [lastUpdatedText, setLastUpdatedText] = useState<string | null>(
    lastUpdatedTextFor(queryClient, queryKey)
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setLastUpdatedText(lastUpdatedTextFor(queryClient, queryKey));
    }, 1000);
    return () => clearInterval(timer);
  }, [queryClient, queryKey]);

  return (
    <span className="overflow-visible text-nowrap text-sm text-primary/60">{lastUpdatedText}</span>
  );
};

const lastUpdatedTextFor = (queryClient: QueryClient, queryKey: string[]) => {
  const { dataUpdatedAt, fetchStatus } = queryClient.getQueryState(queryKey) ?? {};
  const isRefetching = fetchStatus === "fetching";
  if (isRefetching) {
    return "Updating...";
  } else if (!dataUpdatedAt) {
    return null;
  } else {
    const seconds = Duration.between(Instant.ofEpochMilli(dataUpdatedAt), Instant.now()).seconds();
    const roundedMinutes = Math.round(seconds / 60);
    const minutesText = roundedMinutes === 1 ? "minute" : "minutes";
    return `Last updated: ${seconds < 30 ? "just now" : `${roundedMinutes} ${minutesText} ago`}`;
  }
};

export default PatientListPage;
